/* sugar:assets/marquee.js@2.0.0 */
/**
 * Marquee — scrolling tracks and full-window width.
 *
 * One copy of this file serves every marquee on the page. The markup carries
 * data attributes only, so nothing here depends on class names:
 *
 *   [data-marquee-track]  a strip that scrolls on its own
 *   [data-marquee-list]   the flex row inside it; its children are the items
 *   [data-marquee-when]   the breakpoint the strip scrolls at: all | desktop | mobile
 *   [data-marquee-speed]  px per frame at 60fps; negative runs right
 *   [data-marquee-pause]  "true" pauses the strip while the pointer is over it (CSS)
 *   [data-marquee-bleed]  a marquee that escapes its column to the window edge
 *
 * The motion is a CSS keyframe on the list (base.css, `sugar-marquee-scroll`), so
 * the browser moves it on the compositor and the main thread does nothing per
 * frame. This file only measures: it clones the items until the row covers the
 * viewport plus one full set, then writes the distance of one set (--mq-shift)
 * and the time it takes at the chosen speed (--mq-dur). The row is laid out by
 * CSS from the first paint, so nothing stacks or jumps while this loads.
 */

(function () {
  'use strict';

  if (window.SugarMarquee) return;

  var MIN_SETS = 2;
  var REMEASURE_MS = 80;

  function listOf(root) {
    return root.querySelector('[data-marquee-list]');
  }

  // Items only: a block's stylesheet <link> (or a stray script/style) can sit in the list
  // beside its item, and counting it would throw the loop distance off by a gap per link.
  var NOT_ITEM = { LINK: 1, SCRIPT: 1, STYLE: 1, TEMPLATE: 1 };

  function originals(list) {
    return Array.prototype.filter.call(list.children, function (el) {
      return !NOT_ITEM[el.tagName] && !el.hasAttribute('data-marquee-fill');
    });
  }

  function emptyFill(list) {
    var copies = list.querySelectorAll('[data-marquee-fill]');
    for (var i = 0; i < copies.length; i++) copies[i].remove();
  }

  function stop(list) {
    list.classList.remove('is-scrolling', 'is-reverse');
    list.style.removeProperty('--mq-shift');
    list.style.removeProperty('--mq-dur');
  }

  /**
   * The width of one full set of items, margins included. Items are measured one
   * by one so a transform on the list (the animation in flight) cannot skew it.
   */
  function measureSet(list) {
    var items = originals(list);
    if (!items.length) return 0;
    // Every item carries the same gap margin (a track setting), so read it once.
    var cs = getComputedStyle(items[0]);
    var gap = (parseFloat(cs.marginLeft) || 0) + (parseFloat(cs.marginRight) || 0);
    var total = 0;
    items.forEach(function (el) {
      total += el.getBoundingClientRect().width + gap;
    });
    return total;
  }

  /**
   * Clone whole sets until the row is at least one set longer than the viewport,
   * so the loop point is never on screen and the strip never shows a gap.
   */
  // The viewport width comes from measureTrack, so filling one strip never makes the browser lay
  // the page out again after another strip has been rebuilt.
  function fill(list, setWidth, viewport, still) {
    // A strip standing still only has to cover its viewport; a moving one needs a set more.
    var sets = still
      ? Math.max(1, Math.ceil(viewport / setWidth))
      : Math.max(MIN_SETS, Math.ceil((viewport + setWidth) / setWidth));
    var items = originals(list);
    for (var s = 1; s < sets; s++) {
      items.forEach(function (el) {
        var copy = el.cloneNode(true);
        copy.setAttribute('data-marquee-fill', '');
        copy.setAttribute('aria-hidden', 'true');
        // The editor targets a block by this handle; a copy must not answer for it.
        copy.removeAttribute('data-shopify-editor-block');
        list.appendChild(copy);
      });
    }
  }

  /**
   * An SVG with no width/height attributes has no intrinsic size, so `width: auto`
   * resolves to zero and the logo renders as nothing. Once the file has loaded the
   * browser does know its ratio, so read it back and write real pixels.
   */
  function fixIntrinsicSize(root) {
    var images = root.querySelectorAll('[data-marquee-list] img');

    Array.prototype.forEach.call(images, function (img) {
      var size = function () {
        if (!img.naturalWidth || img.offsetWidth !== 0) return;
        // The strip sets a height and caps how far a small file may be stretched to meet
        // it. Read both: the height is the target, max-height only bites on a bitmap
        // saved too small, and a file with no intrinsic size is a vector, so it fills.
        var cs = getComputedStyle(img);
        var target = parseFloat(cs.height) || parseFloat(cs.maxHeight) || 48;
        var ceiling = parseFloat(cs.maxHeight);
        var height = ceiling ? Math.min(target, ceiling) : target;
        img.style.width = Math.round(height * (img.naturalWidth / img.naturalHeight)) + 'px';
        img.style.height = height + 'px';
      };

      if (img.complete) {
        size();
      } else if (!img._marqueeSized) {
        img._marqueeSized = true;
        img.addEventListener('load', function () {
          size();
          // A late image changes the set width, so the loop distance has to follow.
          scheduleRemeasure(root);
        });
      }
    });
  }

  /**
   * Whether this strip scrolls at the current width. A strip that is a plain row at
   * this breakpoint is left as markup so CSS can lay it out however it likes.
   */
  function scrollsHere(root) {
    var when = root.getAttribute('data-marquee-when') || 'all';
    if (when === 'all') return true;
    return when === 'mobile' ? window.innerWidth < 750 : window.innerWidth >= 750;
  }

  /**
   * The reads for one strip: whether it scrolls here, how wide one set is and
   * how wide its viewport is. Nothing is written, so every strip on the page
   * can be measured against one layout before any of them is rebuilt.
   */
  function measureTrack(root) {
    var list = listOf(root);
    if (!list) return null;
    var here = scrollsHere(root);
    // A visitor who asks for reduced motion gets the strip standing still, still filled
    // edge to edge: one short set would leave a full-width strip empty on its right.
    var still = here && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var setWidth = here ? Math.round(measureSet(list)) : 0;
    var viewport = list.parentElement ? list.parentElement.clientWidth : 0;
    return { root: root, list: list, scrolls: here && !still, still: still, setWidth: setWidth, viewport: viewport };
  }

  function applyTrack(m) {
    var root = m.root;
    var list = m.list;

    // Not scrolling here, or nothing to measure yet (a strip inside a closed drawer,
    // fonts still landing): leave it as the plain row and let the next refresh pick it up.
    if (!(m.scrolls || m.still) || !m.setWidth || m.setWidth < 1) {
      emptyFill(list);
      stop(list);
      root._mq = null;
      return;
    }

    // Fonts, images and resizes all ask for a re-measure; only rebuild the copies when
    // the answer changed. Re-cloning a hundred items is the expensive part.
    var last = root._mq;
    if (last && last.setWidth === m.setWidth && last.viewport === m.viewport && last.still === m.still &&
        (m.still || list.classList.contains('is-scrolling'))) return;
    root._mq = { setWidth: m.setWidth, viewport: m.viewport, still: m.still };

    emptyFill(list);
    stop(list);
    fill(list, m.setWidth, m.viewport, m.still);
    if (m.still) return;

    var speed = parseFloat(root.getAttribute('data-marquee-speed')) || 8;
    // Parity with the previous engine: the speed setting was px per frame at 60fps.
    var pxPerSecond = (Math.abs(speed) * 60) / 18;

    list.style.setProperty('--mq-shift', -Math.round(m.setWidth * 100) / 100 + 'px');
    list.style.setProperty('--mq-dur', (m.setWidth / pxPerSecond).toFixed(2) + 's');
    list.classList.toggle('is-reverse', speed < 0);
    list.classList.add('is-scrolling');
  }

  /**
   * Every strip is measured, then every strip is rebuilt. A rebuild in between
   * would make the browser lay the page out again for the next measurement,
   * once per strip.
   */
  function initTracks(roots) {
    var measured = [];
    var i;
    for (i = 0; i < roots.length; i++) fixIntrinsicSize(roots[i]);
    for (i = 0; i < roots.length; i++) {
      var m = measureTrack(roots[i]);
      if (m) measured.push(m);
    }
    for (i = 0; i < measured.length; i++) applyTrack(measured[i]);
  }

  function initTrack(root) {
    initTracks([root]);
  }

  // Late images and fonts each ask for a re-measure; the asks collect for a
  // moment and run as one pass.
  var remeasurePending = [];
  var remeasureTimer = null;

  function scheduleRemeasure(root) {
    if (remeasurePending.indexOf(root) === -1) remeasurePending.push(root);
    clearTimeout(remeasureTimer);
    remeasureTimer = setTimeout(function () {
      var roots = remeasurePending.splice(0);
      initTracks(roots.filter(function (r) { return r.isConnected; }));
    }, REMEASURE_MS);
  }

  /**
   * Full-window width, measured rather than assumed.
   *
   * `calc(50% - 50vw)` only lands correctly when the marquee's column is
   * centred in the window; in a product buy box or any side column it is off by
   * however far that column sits from centre. Reading the element's own
   * position and correcting the margin by that amount works from anywhere, and
   * clientWidth excludes the scrollbar that 100vw would have overshot by.
   *
   * The stylesheet sets --mq-bleed-on inside the breakpoint where the marquee
   * is meant to stretch, so this only fires when it is actually stretching.
   */
  function fitBleed(root) {
    if (getComputedStyle(root).getPropertyValue('--mq-bleed-on').trim() !== '1') {
      root.style.removeProperty('--mq-bleed-w');
      root.style.removeProperty('--mq-bleed-ml');
      return;
    }

    root.style.setProperty('--mq-bleed-w', document.documentElement.clientWidth + 'px');

    var current = parseFloat(getComputedStyle(root).marginLeft) || 0;
    var offset = root.getBoundingClientRect().left;
    root.style.setProperty('--mq-bleed-ml', current - offset + 'px');

    var section = root.closest('.shopify-section');
    if (section) section.style.overflowX = 'clip';
  }

  function fitAllBleed() {
    document.querySelectorAll('[data-marquee-bleed]').forEach(fitBleed);
  }

  /**
   * `scope` narrows the sweep to one subtree. A strip that was hidden when the page
   * loaded — one inside the cart drawer — can be measured on its own without
   * restarting every other marquee on the page from the top. Event handlers pass
   * their event here, so anything without querySelectorAll falls back to the
   * whole document.
   */
  function refresh(scope) {
    if (window.__swOk === false) return;

    fitAllBleed();

    var root = scope && scope.querySelectorAll ? scope : document;
    var tracks = Array.prototype.slice.call(root.querySelectorAll('[data-marquee-track]'));
    if (root !== document && root.matches && root.matches('[data-marquee-track]')) tracks.unshift(root);
    initTracks(tracks);
  }

  var resizeTimer = null;

  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      // The fill count and the loop distance both follow the viewport width.
      refresh();
    }, 150);
  });

  window.SugarMarquee = { refresh: refresh };

  // A strip built inside a collapsed panel measured a zero-width row, so the
  // fill count came out wrong and stayed wrong. The panel says when it is first
  // shown; rebuild just the strips it contains.
  document.addEventListener('sugar:visible', function (event) {
    if (event.target && event.target.querySelectorAll) refresh(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { refresh(); });
  } else {
    refresh();
  }

  // Web fonts and images settle the widths after the first paint; measure again then.
  function remeasureAll() {
    document.querySelectorAll('[data-marquee-track]').forEach(scheduleRemeasure);
  }
  window.addEventListener('load', remeasureAll);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(remeasureAll);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function () {
      setTimeout(refresh, 100);
    });
    document.addEventListener('shopify:block:select', function () {
      setTimeout(refresh, 100);
    });
    document.addEventListener('shopify:section:reorder', function () {
      setTimeout(refresh, 100);
    });
  }
})();
